import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { takeRecoveryCodeSignIn } from './recoveryCodeSignIn';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// After a recovery-code sign-in: how many codes are left, and what to do
// before they run out. Nobody was told before, so a sole admin who lost their
// phone used the codes up one by one.

/** Where two-step sign-in (and "Get new recovery codes") lives. */
export const TWO_STEP_SETTINGS_PATH = '/settings/security';

export function recoveryCodeMessage(left: number | null): string {
  if (left === 0) return 'You signed in with your last recovery code.';
  const count = left === null ? '' : left === 1 ? ' You have 1 left.' : ` You have ${left} left.`;
  return `You signed in with a recovery code.${count}`;
}

// The desktop and connector sign-in pages finish in a moment and close: the
// notice waits for the next page in this tab rather than covering the consent.
const HANDOFF_PREFIXES = ['/oauth/desktop', '/oauth/authorize'];

export function RecoveryCodeNotice() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [left, setLeft] = useState<number | null | undefined>(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open || !user?.id || HANDOFF_PREFIXES.some((p) => pathname.startsWith(p))) return;
    const notice = takeRecoveryCodeSignIn(user.id);
    if (!notice) return;
    setLeft(notice.left);
    setOpen(true);
  }, [pathname, open, user?.id]);

  if (left === undefined) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Signed in with a recovery code</DialogTitle>
          <DialogDescription>{recoveryCodeMessage(left)}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Each recovery code works once. If you still have your phone, get a fresh set in Settings → Security.
          If you’ve lost it, ask your organisation’s admin to reset your two-step sign-in, then set it up on your new phone before your codes run out.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Later</Button>
          <Button onClick={() => { setOpen(false); navigate(TWO_STEP_SETTINGS_PATH); }}>
            Go to Security settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RecoveryCodeNotice;
